import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagsCardService } from './tags-card.service';

@Component({
    selector: 'app-tags-card',
    templateUrl: './tags-card.component.html',
    styleUrls: ['./tags-card.component.scss'],
    // None so styles still apply after the popup is moved to document.body.
    encapsulation: ViewEncapsulation.None,
})
export class TagsCardComponent implements AfterViewInit, OnDestroy {
    @Input()
    tags: MaxBrainTag[];

    // caller passes a template ref to a DOM element (e.g. #more)
    @Input()
    target: HTMLElement;

    /**
     * Prefer opening above the trigger (e.g. course card footer).
     * Still flips to below when there is not enough viewport space.
     */
    @Input()
    openAbove = false;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    hide = true;

    @ViewChild('card')
    card: ElementRef<HTMLElement>;

    private _isCursorOnCard = false;
    private _isCursorOnTarget = false;
    private _hideTimeout: ReturnType<typeof setTimeout> | null = null;
    private _unlisteners: Array<() => void> = [];
    private _hostParent: Node | null = null;
    private _hostNextSibling: ChildNode | null = null;
    private _onViewportChange = (): void => {
        if (!this.hide) {
            this._positionCard();
        }
    };

    constructor(private _renderer: Renderer2, private service: TagsCardService, private _cd: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.listenEvents();
        });
    }

    ngOnDestroy(): void {
        this._clearHideTimeout();
        this._unlisteners.forEach((unlisten) => unlisten());
        this._unlisteners = [];
        window.removeEventListener('scroll', this._onViewportChange, true);
        window.removeEventListener('resize', this._onViewportChange);
        this._restoreCardToHost();
    }

    listenEvents(): void {
        if (!this.target || !this.card?.nativeElement) {
            return;
        }

        window.addEventListener('scroll', this._onViewportChange, true);
        window.addEventListener('resize', this._onViewportChange);

        this._unlisteners.push(
            this._renderer.listen(this.target, 'mouseenter', () => {
                this._isCursorOnTarget = true;
                this._clearHideTimeout();
                this._show();
            })
        );

        this._unlisteners.push(
            this._renderer.listen(this.target, 'mouseleave', () => {
                this._isCursorOnTarget = false;
                this._scheduleHide();
            })
        );

        this._unlisteners.push(
            this._renderer.listen(this.card.nativeElement, 'mouseenter', () => {
                this._isCursorOnCard = true;
                this._clearHideTimeout();
                this._show();
            })
        );

        this._unlisteners.push(
            this._renderer.listen(this.card.nativeElement, 'mouseleave', () => {
                this._isCursorOnCard = false;
                this._scheduleHide();
            })
        );
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    private _show(): void {
        this.service.show(this);
        this._cd.detectChanges();
        // Wait a frame so [hidden] is cleared and the card has a measurable size.
        requestAnimationFrame(() => {
            this._attachCardToBody();
            this._positionCard();
        });
    }

    private _attachCardToBody(): void {
        const el = this.card?.nativeElement;
        if (!el || el.parentElement === document.body) {
            return;
        }

        this._hostParent = el.parentNode;
        this._hostNextSibling = el.nextSibling;
        document.body.appendChild(el);
    }

    private _restoreCardToHost(): void {
        const el = this.card?.nativeElement;
        if (!el || !this._hostParent || el.parentElement !== document.body) {
            this._hostParent = null;
            this._hostNextSibling = null;
            return;
        }

        this._hostParent.insertBefore(el, this._hostNextSibling);
        this._hostParent = null;
        this._hostNextSibling = null;
    }

    private _positionCard(): void {
        const el = this.card?.nativeElement;
        if (!el || !this.target || this.hide) {
            return;
        }

        const gap = 4;
        const targetRect = this.target.getBoundingClientRect();

        // Measure off-screen first so we know the popup size.
        this._renderer.setStyle(el, 'position', 'fixed');
        this._renderer.setStyle(el, 'top', '0px');
        this._renderer.setStyle(el, 'left', '0px');
        this._renderer.setStyle(el, 'bottom', 'auto');
        this._renderer.setStyle(el, 'right', 'auto');
        this._renderer.setStyle(el, 'visibility', 'hidden');

        const cardRect = el.getBoundingClientRect();
        const fitsAbove = targetRect.top >= cardRect.height + gap;
        const fitsBelow = window.innerHeight - targetRect.bottom >= cardRect.height + gap;
        const openUp = this.openAbove ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;

        let top = openUp ? targetRect.top - cardRect.height - gap : targetRect.bottom + gap;
        top = Math.min(Math.max(gap, top), Math.max(gap, window.innerHeight - cardRect.height - gap));

        let left = targetRect.left;
        left = Math.min(Math.max(gap, left), Math.max(gap, window.innerWidth - cardRect.width - gap));

        this._renderer.setStyle(el, 'top', `${top}px`);
        this._renderer.setStyle(el, 'left', `${left}px`);
        this._renderer.setStyle(el, 'visibility', 'visible');
    }

    private _scheduleHide(): void {
        this._clearHideTimeout();
        this._hideTimeout = setTimeout(() => {
            if (!this._isCursorOnCard && !this._isCursorOnTarget && !this.hide) {
                this.service.hide(this);
                this._restoreCardToHost();
                this._cd.detectChanges();
            }
        }, 150);
    }

    private _clearHideTimeout(): void {
        if (this._hideTimeout) {
            clearTimeout(this._hideTimeout);
            this._hideTimeout = null;
        }
    }
}
